/**
 * Xiaomi · Today's Activity Tile · group dashboard top-strip
 *
 * Shows a live preview of today's End-of-Day digest so the CEO has
 * visibility into the Xiaomi 2026 programme's activity throughout the
 * day — not just at midnight when the cron posts to Zoho.
 *
 * Data source: GET /api/xiaomi/eod-digest/preview (no writes · no Zoho)
 *
 * Refresh: every 90 seconds, or on click of the "↻ Refresh" pill.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } });

function Stat({ label, value, accent, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-lg px-3 py-2 flex-1 min-w-[110px]"
      style={{ background: '#faf6ef', border: '1px solid #efe6d3' }}
    >
      <div className="text-[10px] uppercase tracking-wider" style={{ color: '#8a7e6b' }}>
        {label}
      </div>
      <div
        className="text-2xl font-bold leading-none mt-0.5"
        style={{ color: accent || '#15110d', fontFamily: 'Fraunces, serif' }}
      >
        {value ?? 0}
      </div>
    </div>
  );
}

export default function XiaomiTodayTile({ embedded = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [yesterday, setYesterday] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const [today, yday] = await Promise.all([
        axios.get(`${API}/xiaomi/eod-digest/preview?offset_days=0`, auth()),
        axios.get(`${API}/xiaomi/eod-digest/preview?offset_days=-1`, auth()),
      ]);
      setData(today.data?.preview || null);
      setYesterday(yday.data?.preview || null);
    } catch (e) {
      // Quietly hide on 403 — many users aren't xiaomi-access
      if (e?.response?.status === 403) {
        setData('forbidden');
      } else {
        setErr(e?.response?.data?.detail || 'Failed to load');
      }
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 90_000);
    return () => clearInterval(t);
  }, [load]);

  if (data === 'forbidden') return null;
  if (loading && !data) return null;
  if (err) {
    return (
      <div className="rounded-xl border border-line p-4 text-sm text-soft">
        Xiaomi Today · {err}
      </div>
    );
  }
  if (!data) return null;

  const totalActivity =
      (data.instances_opened || 0) + (data.instances_closed || 0)
    + (data.gift_ops_created || 0) + (data.couriered_today || 0)
    + (data.delivered_today || 0) + (data.audit_edits || 0)
    + (data.macros_applied || 0) + (data.inbound_wa || 0)
    + (data.eligibility_imports || 0);

  return (
    <div
      data-testid="xiaomi-today-tile"
      className="rounded-xl border p-5 space-y-3 mb-5"
      style={{ background: 'linear-gradient(180deg,#fffaf3 0%,#faf6ef 100%)',
               borderColor: '#efe6d3' }}
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <span
              className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full"
              style={{ background: '#15110d', color: '#ff6900', letterSpacing: '0.12em' }}
            >
              Today · Xiaomi 2026
            </span>
            <span className="text-sm text-soft">{data.ist_date}</span>
          </div>
          <h2 className="mt-1" style={{ fontFamily: 'Fraunces, serif', fontSize: 20 }}>
            {totalActivity === 0
              ? 'Quiet day so far — nothing material has happened on the programme yet.'
              : `${totalActivity} events captured today`}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          {!embedded && (
            <Link
              to="/xiaomi"
              className="px-3 py-1.5 rounded-lg bg-[#15110d] text-[#f3e6cf] text-xs hover:opacity-90"
              data-testid="xiaomi-today-tile-open-dashboard"
            >
              Open Xiaomi
            </Link>
          )}
          <button
            onClick={load}
            disabled={loading}
            className="px-3 py-1.5 rounded-lg bg-white border border-line text-xs text-soft hover:bg-[#faf6ef]"
            data-testid="xiaomi-today-tile-refresh"
          >
            {loading ? '…' : '↻'}
          </button>
        </div>
      </div>

      {/* Partner utilisation */}
      <div>
        <div className="text-[11px] uppercase tracking-wider text-soft mb-1.5">
          Partner utilisation
        </div>
        <div className="flex flex-wrap gap-2">
          <Stat label="Opened" value={data.instances_opened} testid="xtt-opened" />
          <Stat label="Closed" value={data.instances_closed} accent="#128c7e" testid="xtt-closed" />
          <Stat label="Gifts created" value={data.gift_ops_created} testid="xtt-gift-ops" />
          <Stat label="Ordered" value={data.ordered_today} testid="xtt-ordered" />
          <Stat label="Dispatched" value={data.couriered_today} accent="#d98a1f" testid="xtt-dispatched" />
          <Stat label="Delivered" value={data.delivered_today} accent="#128c7e" testid="xtt-delivered" />
          <Stat label="Approved" value={data.approved_today} accent="#128c7e" testid="xtt-approved" />
          <Stat label="Rejected" value={data.rejected_today} accent="#c0492f" testid="xtt-rejected" />
        </div>
      </div>

      {/* ScaleBoard + Zoho activity */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-soft mb-1.5">
            ScaleBoard activity
          </div>
          <div className="flex flex-wrap gap-2">
            <Stat label="Audit edits" value={data.audit_edits} testid="xtt-audit" />
            <Stat label="Eligibility imports" value={data.eligibility_imports} testid="xtt-elig" />
            <Stat label="Outbound WA" value={data.outbound_wa} testid="xtt-out-wa" />
          </div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-soft mb-1.5">
            Zoho activity
          </div>
          <div className="flex flex-wrap gap-2">
            <Stat label="Inbound WA" value={data.inbound_wa}
                  testid="xtt-in-wa" />
            <Stat label="Macros fired" value={(data.macros_applied || 0) + (data.macros_skipped || 0)}
                  testid="xtt-macros" />
            <Stat label="Workflow events" value={data.workflow_total}
                  testid="xtt-workflow" />
          </div>
        </div>
      </div>

      {data.top_dealers?.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-soft hover:text-ink">
            Top {data.top_dealers.length} most-active partners today
          </summary>
          <div className="mt-2 grid grid-cols-2 md:grid-cols-5 gap-2">
            {data.top_dealers.map((td) => (
              <div key={td.dealer_id}
                   className="rounded p-2 text-[11px]"
                   style={{ background: '#fff', border: '1px solid #efe6d3' }}>
                <div className="font-mono text-soft">{td.dealer_id}</div>
                <div className="font-semibold truncate" title={td.outlet}>
                  {td.outlet || '—'}
                </div>
                <div className="text-soft truncate" title={td.owner}>
                  {td.owner || '—'} · <b>{td.n}</b> events
                </div>
              </div>
            ))}
          </div>
        </details>
      )}

      {/* Yesterday comparison + open-digest link */}
      <div className="flex items-center justify-between text-[11px] text-soft pt-2"
           style={{ borderTop: '1px dashed #efe6d3' }}>
        <span>
          Posts to Zoho at midnight IST · cron <code>xiaomi_eod_digest</code>
          {yesterday && yesterday.workflow_total !== undefined && (
            <> · yesterday: <b>{yesterday.workflow_total}</b> workflow
            events · <b>{yesterday.audit_edits}</b> audit edits</>
          )}
        </span>
        {!embedded && (
          <Link to="/xiaomi" className="hover:text-ink underline">
            Full Xiaomi dashboard →
          </Link>
        )}
      </div>
    </div>
  );
}
